import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../components/Layout';
import DataTable from '../../components/DataTable';
import DebouncedSearchInput from '../../components/DebouncedSearchInput';
import chartOfAccountsAPI from '../../api/chartOfAccountsAPI';
import { exportChartOfAccounts } from '../../features/chart-of-accounts/api/exportCoa';
import { useAuth } from '../../context/AuthContext';


const ChartOfAccountsList = () => {
    const storeId = localStorage.getItem('storeId') || '';
    const { isAuthReady } = useAuth();
    const [allAccounts, setAllAccounts] = useState([]);
    const [filteredAccounts, setFilteredAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('active');
    const [systemFilter, setSystemFilter] = useState('all');
    const [exporting, setExporting] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!isAuthReady) return;
        fetchAccounts();
    }, [isAuthReady, storeId]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const system = (params.get('system') || '').toLowerCase();

        if (system === '1' || system === 'true' || system === 'system') {
            setSystemFilter('system');
            return;
        }

        if (system === '0' || system === 'false' || system === 'non-system' || system === 'nonsystem') {
            setSystemFilter('non-system');
            return;
        }

        setSystemFilter('all');
    }, [location.search]);

    useEffect(() => {
        filterAccounts();
    }, [searchTerm, typeFilter, statusFilter, systemFilter, allAccounts]);

    const fetchAccounts = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await chartOfAccountsAPI.getAll(null, null, {
                activeOnly: false,
                populate: 'accountType,parentAccount'
            });

            const accounts = response.data.map(account => ({
                ...account,
                accountType: account.accountType || { name: 'N/A' },
                parentAccount: account.parentAccount || null
            }));

            setAllAccounts(accounts);
        } catch (error) {
            setError(error.message || 'Failed to load chart of accounts');
            setAllAccounts([]);
        } finally {
            setLoading(false);
        }
    };

    // The CSV must contain exactly the rows on screen, so every filter the table
    // applies client-side is forwarded to the server.
    const handleExport = async () => {
        try {
            setExporting(true);
            setError(null);
            await exportChartOfAccounts(null, {
                status: statusFilter,
                type: typeFilter,
                system: systemFilter,
                search: searchTerm,
            });
        } catch (err) {
            setError(err.message || 'Failed to export chart of accounts');
        } finally {
            setExporting(false);
        }
    };

    const handleDeactivateAccount = async (accountId) => {
        try {
            await chartOfAccountsAPI.delete(accountId);
            setAllAccounts(allAccounts.map(account => 
                account._id === accountId ? { ...account, isActive: false } : account
            ));
        } catch (err) {
            setError(err.message || 'Failed to deactivate account');
        }
    };


    const filterAccounts = () => {
        let filtered = [...allAccounts];

        // Apply type filter
        if (typeFilter !== 'all') {
            filtered = filtered.filter(account => 
                account.accountType?.name?.toLowerCase() === typeFilter.toLowerCase()
            );
        }

        // Apply status filter
        if (statusFilter === 'active') {
            filtered = filtered.filter(account => account.isActive);
        } else if (statusFilter === 'inactive') {
            filtered = filtered.filter(account => !account.isActive);
        }

        // Apply system filter
        if (systemFilter === 'system') {
            filtered = filtered.filter(account => account.isSystemAccount);
        } else if (systemFilter === 'non-system') {
            filtered = filtered.filter(account => !account.isSystemAccount);
        }

        // Apply search term filter
        if (searchTerm) {
            const lowercasedSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(account => 
                (account.accountNumber?.toLowerCase() || '').includes(lowercasedSearch) ||
                (account.accountName?.toLowerCase() || '').includes(lowercasedSearch) ||
                (account.accountType?.name?.toLowerCase() || '').includes(lowercasedSearch) ||
                (account.description?.toLowerCase() || '').includes(lowercasedSearch)
            );
        }

        setFilteredAccounts(filtered);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const typeBadgeClass = (typeName) => {
        switch (typeName?.toLowerCase()) {
            case 'asset':
                return 'bg-blue-100 text-blue-800';
            case 'liability':
                return 'bg-red-100 text-red-800';
            case 'equity':
                return 'bg-green-100 text-green-800';
            case 'revenue':
                return 'bg-purple-100 text-purple-800';
            case 'expense':
                return 'bg-orange-100 text-orange-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const balanceBadgeClass = (balanceType) => {
        return balanceType === 'debit' 
            ? 'bg-teal-100 text-teal-800'
            : 'bg-indigo-100 text-indigo-800';
    };

    const statusBadgeClass = (isActive) => {
        return isActive 
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-800';
    };

    const columns = [
        {
            title: 'Account Number',
            key: 'accountNumber',
            render: (account) => (
                <div>
                    <span className="font-mono text-sm">{account.accountNumber}</span>
                </div>
            )
        },
        {
            title: 'Account Name',
            key: 'accountName',
            render: (account) => (
                <div>
                    <div className="flex items-center gap-2">
                        <div className="font-medium">{account.accountName}</div>
                        {account.isSystemAccount && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-100 text-yellow-800">
                                System
                            </span>
                        )}
                    </div>
                    {account.description && (
                        <div className="text-xs text-gray-500 truncate max-w-xs">
                            {account.description}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Type',
            key: 'accountType',
            render: (account) => (
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${typeBadgeClass(account.accountType?.name)}`}>
                    {account.accountType?.name || 'N/A'}
                </span>
            )
        },
        {
            title: 'Normal Balance',
            key: 'normalBalance',
            render: (account) => (
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${balanceBadgeClass(account.normalBalance)}`}>
                    {account.normalBalance}
                </span>
            )
        },
        {
            title: 'Current Balance',
            key: 'currentBalance',
            render: (account) => (
                <div className="text-right font-mono text-sm">
                    {account.currentBalance?.toFixed(2) || '0.00'}
                </div>
            )
        },
        {
            title: 'Status',
            key: 'isActive',
            render: (account) => (
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusBadgeClass(account.isActive)}`}>
                    {account.isActive ? 'Active' : 'Inactive'}
                </span>
            )
        }
    ];

    // Get unique account types for filter
    const accountTypes = [...new Set(allAccounts
        .map(acc => acc.accountType?.name)
        .filter(Boolean)
    )];

    return (
        <Layout pageTitle="Chart of Accounts">
            <div className="bg-gray-100 min-h-screen p-6">

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-4">
                        <DebouncedSearchInput
                            placeholder="Search by account number, name, type or description..."
                            onSearch={handleSearch}
                            className="flex-1 mr-4 max-w-md"
                        />

                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        >
                            <option value="all">All Types</option>
                            {accountTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>

                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="all">All Status</option>
                        </select>

                        <select
                            value={systemFilter}
                            onChange={(e) => setSystemFilter(e.target.value)}
                            className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        >
                            <option value="all">All Accounts</option>
                            <option value="system">System</option>
                            <option value="non-system">Non-system</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleExport}
                            disabled={exporting}
                            className="transition-all duration-300 px-4 py-2 border border-primary-500 text-primary-600 rounded hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                            {exporting ? 'Exporting…' : 'Export CSV'}
                        </button>

                        <button 
                            onClick={() => navigate('/client/chart-of-accounts/new')}
                            className="transition-all duration-300 px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 flex items-center"
                        >
                            <span className="mr-1">+</span> New Account
                        </button>
                    </div>
                </div>

                <div className="mb-2">
                    {searchTerm && (
                        <div className="text-sm text-gray-500">
                            Showing {filteredAccounts.length} of {allAccounts.length} accounts matching "{searchTerm}"
                        </div>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                        System accounts can be opened and updated, but core fields are protected.
                    </div>
                </div>

                <DataTable
                    columns={columns}
                    data={filteredAccounts}
                    loading={loading}
                    emptyMessage="No accounts found"
                    onRowClick={(account) => navigate(`/client/chart-of-accounts/${account._id}`)}
                    onEdit={(account) => navigate(`/client/chart-of-accounts/${account._id}`)}
                    onDelete={(account) => {
                        if (window.confirm(`Are you sure you want to deactivate account ${account.accountNumber}?`)) {
                            handleDeactivateAccount(account._id);
                        }
                    }}
                    disableDelete={(account) => !account.isActive || account.isSystemAccount}
                    deleteTooltip={(account) => 
                        account.isSystemAccount ? 'System accounts cannot be deleted' : 'Deactivate account'
                    }
                />
            </div>
        </Layout>
    );
};

export default ChartOfAccountsList;